/**
 * PD Interactive Compare ("Find your system")
 * FOI-465 — ported from the client's system-selector-v2.html prototype.
 *
 * Logic is unchanged from the prototype: every interaction selects one of the
 * real configs (tier × battery). Dragging a slider snaps to the nearest config
 * on that axis, sticky to the current config on the other axes. Lock pins a
 * value and greys out configs it rules out. Data comes from section blocks via
 * an inline JSON script tag, not from the store.
 *
 * Native controls only: <input type="range">, <input type="number">, <button>,
 * <input type="checkbox" role="switch">, <fieldset disabled> for the weight gate,
 * <details> for the mobile sticky bar.
 */
(function () {
  if (customElements.get("pd-interactive-compare")) return;

  var STICKY_WEIGHT = 0.15; // weight of the "stay close to current config" term

  function toNumber(v) {
    if (v === null || v === undefined || v === "") return null;
    var n = parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
    return isNaN(n) ? null : n;
  }

  function fill(template, vars) {
    return String(template || "").replace(/\{(\w+)\}/g, function (_, k) {
      return vars[k] !== undefined && vars[k] !== null ? vars[k] : "";
    });
  }

  class PdInteractiveCompare extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.setup();
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
    }

    /* ------------------------------------------------------------ setup */
    setup() {
      var dataEl = this.querySelector("[data-selector-config]");
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      var d = this.data;
      this.text = d.text || {};
      this.tiers = d.tiers || {};
      this.locale = d.locale || undefined;

      this.configs = (d.configs || [])
        .map(function (c) {
          return {
            tier: c.tier,
            battery: c.battery || "",
            batteryLabel: c.batteryLabel || "",
            capacity: toNumber(c.capacity),
            thrust: toNumber(c.thrust),
            weight: toNumber(c.weight),
            cost: toNumber(c.cost),
            comingSoon: c.comingSoon === true,
            link: c.link || "",
          };
        })
        .filter(function (c) {
          return c.capacity !== null && c.thrust !== null && c.weight !== null;
        });

      if (!this.configs.length) {
        this.hidden = true;
        return;
      }

      // Axis bounds: use the fixed axis from the data when given (matches the
      // client prototype), otherwise derive from the data with a small margin
      // so the detents never sit on the caps.
      this.metrics = (d.metrics || []).map(function (m) {
        if (Array.isArray(m.axis) && m.axis.length === 2) {
          return Object.assign({}, m, { axis: [toNumber(m.axis[0]), toNumber(m.axis[1])] });
        }
        var vals = this.configs.map(function (c) {
          return c[m.key];
        });
        var min = Math.min.apply(null, vals);
        var max = Math.max.apply(null, vals);
        var span = max - min || Math.abs(max) || 1;
        return Object.assign({}, m, { axis: [min - span * 0.12, max + span * 0.12] });
      }, this);

      this.threshold = toNumber(d.threshold) || 90;
      this.topTier = this.configs.reduce(function (best, c) {
        return c.thrust > best.thrust ? c : best;
      }, this.configs[0]).tier;

      this.locks = {};
      this.selected = Math.min(this.configs.length - 1, Math.max(0, toNumber(d.defaultIndex) !== null ? toNumber(d.defaultIndex) : Math.floor((this.configs.length - 1) / 2)));

      this.cacheElements();
      this.buildMetrics();
      this.bind();
      this.render();
      this.observeViewport();
    }

    cacheElements() {
      this.form = this.querySelector("form");
      this.rider = this.querySelector("[data-rider]");
      this.gateHint = this.querySelector("[data-gate-hint]");
      this.gated = this.querySelector("[data-gated]");
      this.costToggle = this.querySelector("[data-cost-toggle]");
      this.sticky = this.querySelector("[data-sticky]");
      this.metricEls = {};
      var self = this;
      this.querySelectorAll("[data-metric]").forEach(function (el) {
        self.metricEls[el.getAttribute("data-metric")] = el;
      });
    }

    /* Range inputs are configured from the data; detents are laid out once. */
    buildMetrics() {
      var self = this;
      this.metrics.forEach(function (m) {
        var el = self.metricEls[m.key];
        if (!el) return;
        m.el = el;
        m.range = el.querySelector("[data-range]");
        m.output = el.querySelector("[data-output]");
        m.lock = el.querySelector("[data-lock]");
        m.detents = el.querySelector("[data-detents]");
        if (!m.range) return;

        m.range.min = m.axis[0];
        m.range.max = m.axis[1];
        m.range.step = "any";

        if (m.detents) {
          m.detents.innerHTML = "";
          self.configs.forEach(function (c, i) {
            var dot = document.createElement("span");
            dot.className = "pd-ic__detent";
            dot.setAttribute("data-detent", i);
            dot.style.setProperty("--f", self.frac(c[m.key], m.axis));
            m.detents.appendChild(dot);
          });
        }
      });
    }

    bind() {
      var self = this;

      if (this.form) {
        this.form.addEventListener("submit", function (e) {
          e.preventDefault();
        });
      }

      if (this.rider) {
        this.rider.addEventListener("input", function () {
          self.render();
        });
      }

      if (this.costToggle) {
        this.costToggle.addEventListener("change", function () {
          self.render();
        });
      }

      this.querySelectorAll("[data-preset]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var idx = self.preset(btn.getAttribute("data-preset"));
          if (idx !== null && idx !== undefined) {
            self.selected = idx;
            self.render();
          }
        });
      });

      this.metrics.forEach(function (m) {
        if (!m.range) return;

        // Pointer / touch drag: snap to the nearest real config on this axis.
        m.range.addEventListener("input", function () {
          if (self.isLocked(m.key)) return;
          var target = parseFloat(m.range.value);
          var idx = self.pick(m.key, target);
          if (idx !== self.selected) {
            self.selected = idx;
            self.render();
          } else {
            // keep the thumb on the detent even if the config did not change
            m.range.value = self.configs[self.selected][m.key];
          }
        });

        // Keyboard: step between distinct values on this axis.
        m.range.addEventListener("keydown", function (e) {
          if (self.isLocked(m.key)) return;
          var dir = 0;
          if (e.key === "ArrowRight" || e.key === "ArrowUp" || e.key === "PageUp") dir = 1;
          if (e.key === "ArrowLeft" || e.key === "ArrowDown" || e.key === "PageDown") dir = -1;
          var edge = e.key === "Home" ? -1 : e.key === "End" ? 1 : 0;
          if (!dir && !edge) return;
          e.preventDefault();

          var values = self.distinctValues(m.key);
          if (!values.length) return;
          var cur = self.configs[self.selected][m.key];
          var pos = values.indexOf(cur);
          var next;
          if (edge) {
            next = edge < 0 ? values[0] : values[values.length - 1];
          } else {
            next = values[Math.max(0, Math.min(values.length - 1, pos + dir))];
          }
          var idx = self.pick(m.key, next);
          if (idx !== self.selected) {
            self.selected = idx;
            self.render();
          }
        });

        if (m.lock) {
          m.lock.addEventListener("click", function () {
            if (self.isLocked(m.key)) {
              delete self.locks[m.key];
            } else {
              self.locks[m.key] = self.configs[self.selected][m.key];
            }
            self.render();
          });
        }
      });
    }

    /* Mobile sticky bar is only shown while the selector is on screen. */
    observeViewport() {
      var self = this;
      if (!this.sticky || !("IntersectionObserver" in window)) return;
      this._io = new IntersectionObserver(
        function (entries) {
          self.inView = entries[0].isIntersecting;
          self.updateSticky();
        },
        { threshold: 0.05 }
      );
      this._io.observe(this);
    }

    /* ------------------------------------------------------------ logic */
    frac(v, axis) {
      return Math.max(0, Math.min(1, (v - axis[0]) / (axis[1] - axis[0])));
    }

    span(key) {
      var m = this.metrics.find(function (x) {
        return x.key === key;
      });
      return m ? m.axis[1] - m.axis[0] || 1 : 1;
    }

    isLocked(key) {
      return this.locks[key] !== undefined;
    }

    isValid(i) {
      var c = this.configs[i];
      var self = this;
      return Object.keys(this.locks).every(function (k) {
        return c[k] === self.locks[k];
      });
    }

    validIdx() {
      var self = this;
      return this.configs
        .map(function (_, i) {
          return i;
        })
        .filter(function (i) {
          return self.isValid(i);
        });
    }

    distinctValues(key) {
      var self = this;
      var vals = this.validIdx().map(function (i) {
        return self.configs[i][key];
      });
      return vals
        .filter(function (v, i, a) {
          return a.indexOf(v) === i;
        })
        .sort(function (a, b) {
          return a - b;
        });
    }

    /* Nearest config on `key`, sticky to the current config on other axes. */
    pick(key, target) {
      var self = this;
      var cur = this.configs[this.selected];
      var best = this.selected;
      var bestScore = Infinity;
      this.validIdx().forEach(function (i) {
        var c = self.configs[i];
        var primary = Math.abs(c[key] - target) / self.span(key);
        var secondary = 0;
        self.metrics.forEach(function (m) {
          if (m.key !== key) secondary += Math.abs(c[m.key] - cur[m.key]) / self.span(m.key);
        });
        var score = primary + STICKY_WEIGHT * secondary;
        if (score < bestScore) {
          bestScore = score;
          best = i;
        }
      });
      return best;
    }

    preset(name) {
      var self = this;
      var valid = this.validIdx();
      if (!valid.length) return null;
      if (name === "runtime") {
        return valid.reduce(function (bi, i) {
          return self.configs[i].capacity > self.configs[bi].capacity ? i : bi;
        }, valid[0]);
      }
      if (name === "power") {
        var maxT = Math.max.apply(
          null,
          valid.map(function (i) {
            return self.configs[i].thrust;
          })
        );
        var cur = this.configs[this.selected];
        var best = valid[0];
        var bd = Infinity;
        valid.forEach(function (i) {
          var c = self.configs[i];
          if (c.thrust !== maxT) return;
          var d = Math.abs(c.capacity - cur.capacity);
          if (d < bd) {
            bd = d;
            best = i;
          }
        });
        return best;
      }
      return null;
    }

    riderWeight() {
      if (!this.rider) return NaN;
      var v = parseFloat(this.rider.value);
      var min = parseFloat(this.rider.min) || 0;
      var max = parseFloat(this.rider.max) || Infinity;
      return !isNaN(v) && v >= min && v <= max ? v : NaN;
    }

    /* ------------------------------------------------------------ format */
    fmt(v) {
      if (v === null || v === undefined) return "";
      return Number.isInteger(v) ? String(v) : v.toFixed(1);
    }

    money(v) {
      if (v === null || v === undefined) return "";
      var n = Math.round(v).toLocaleString(this.locale);
      return (this.text.currencyPrefix || "") + n + (this.text.currencySuffix || "");
    }

    tierMeta(key) {
      return (
        this.tiers[key] || {
          name: key,
          color: "#101212",
          description: "",
          fitGood: "",
        }
      );
    }

    /* ------------------------------------------------------------ render */
    render() {
      var self = this;
      var c = this.configs[this.selected];
      var meta = this.tierMeta(c.tier);
      var rider = this.riderWeight();
      var active = !isNaN(rider);

      this.style.setProperty("--pd-ic-tier", meta.color || "#101212");
      this.classList.toggle("is-gated", !active);
      if (this.gated) this.gated.disabled = !active;
      if (this.gateHint) this.gateHint.hidden = active;

      /* sliders */
      this.metrics.forEach(function (m) {
        if (!m.range) return;
        var locked = self.isLocked(m.key);
        var value = c[m.key];

        m.range.value = value;
        m.range.disabled = !active || locked;
        m.range.setAttribute("aria-valuetext", meta.name + " " + c.battery + ": " + self.fmt(value) + " " + (m.unit || ""));
        m.el.classList.toggle("is-locked", locked);

        if (m.output) m.output.textContent = self.fmt(value);

        if (m.lock) {
          m.lock.setAttribute("aria-pressed", locked ? "true" : "false");
          var label = m.lock.querySelector("[data-lock-label]");
          if (label) label.textContent = locked ? self.text.lockedLabel || "Locked" : self.text.lockLabel || "Lock";
        }

        if (m.detents) {
          m.detents.querySelectorAll("[data-detent]").forEach(function (dot, i) {
            dot.classList.toggle("is-on", i === self.selected);
            dot.classList.toggle("is-off", !self.isValid(i));
          });
        }
      });

      /* quick-pick chips: active when the current setup is that preset's result */
      this.querySelectorAll("[data-preset]").forEach(function (btn) {
        var on = active && self.preset(btn.getAttribute("data-preset")) === self.selected;
        btn.classList.toggle("is-active", on);
        btn.setAttribute("aria-pressed", on ? "true" : "false");
      });

      /* result card(s) — desktop card and mobile sticky panel share fields */
      var costOn = !!(this.costToggle && this.costToggle.checked);
      var batteryLabel = c.batteryLabel || "";

      this.setField("system", meta.name);
      this.setField("battery", batteryLabel);
      this.setField("description", meta.description || "");
      this.setField("spec", this.fmt(c.capacity) + " Wh · " + this.fmt(c.thrust) + " kg " + (this.text.thrustWord || "thrust"));

      /* cost line */
      var costText = "";
      if (costOn && c.cost !== null) {
        costText = fill(this.text.costLine || "Cost: {price}", { price: this.money(c.cost) });
        var lighter = this.configs
          .filter(function (x) {
            return x.tier === c.tier && x.capacity < c.capacity && x.cost !== null;
          })
          .sort(function (a, b) {
            return b.capacity - a.capacity;
          })[0];
        if (lighter && this.text.costSaving) {
          costText +=
            " " +
            fill(this.text.costSaving, {
              battery: lighter.battery,
              saving: this.money(c.cost - lighter.cost),
              weight: (c.weight - lighter.weight).toFixed(1),
            });
        }
      }
      this.setField("cost", costText, true);

      /* "Coming soon" label — shown instead of a price for setups not yet on sale */
      this.setField("comingSoon", c.comingSoon ? this.text.comingSoonLabel || "Coming soon" : "", true);

      /* fit guidance */
      var fitState = "empty";
      var fitText = this.text.fitEmpty || "";
      if (active) {
        var underTiered = rider >= this.threshold && c.tier !== this.topTier;
        if (underTiered) {
          fitState = "warn";
          fitText = fill(this.text.fitWarning || "", {
            tier: meta.name,
            threshold: this.threshold,
            top: this.tierMeta(this.topTier).name,
          });
        } else {
          fitState = "good";
          fitText = meta.fitGood || fill(this.text.fitGood || "", { tier: meta.name, threshold: this.threshold });
        }
      }
      this.querySelectorAll("[data-fit]").forEach(function (el) {
        el.setAttribute("data-fit", fitState);
      });
      this.setField("fit", fitText);

      /* CTA link per outcome */
      this.querySelectorAll("[data-field='link']").forEach(function (a) {
        if (c.link) {
          a.href = c.link;
          a.hidden = false;
        } else {
          a.removeAttribute("href");
          a.hidden = true;
        }
      });

      this.updateSticky(active);
    }

    setField(name, value, hideWhenEmpty) {
      this.querySelectorAll("[data-field='" + name + "']").forEach(function (el) {
        el.textContent = value;
        if (hideWhenEmpty) el.hidden = !value;
      });
    }

    updateSticky(active) {
      if (!this.sticky) return;
      if (active === undefined) active = !isNaN(this.riderWeight());
      var show = active && this.inView !== false;
      this.sticky.hidden = !show;
      if (!show) this.sticky.open = false;
    }
  }

  customElements.define("pd-interactive-compare", PdInteractiveCompare);
})();
